import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { VariantEditor, type VariantData } from "./VariantEditor";
import { ArrowRight, Save, Loader2, ImagePlus, X, Package, Tag, BarChart2, Search } from "lucide-react";

/** Convert NIS string to integer agorot */
function nisToAgorot(nis: string): number {
  const num = parseFloat(nis);
  if (isNaN(num) || num < 0) return 0;
  return Math.round(num * 100);
}

/** Convert agorot to NIS display string */
function agorotToNis(agorot: number | null | undefined): string {
  if (agorot == null || agorot === 0) return "";
  return (agorot / 100).toFixed(2);
}

function FormField({ label, hint, children }: { readonly label: string; readonly hint?: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium leading-none">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ProductFormPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [productType, setProductType] = useState("physical");
  const [status, setStatus] = useState("draft");
  const [price, setPrice] = useState("");
  const [compareAtPrice, setCompareAtPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [weightGrams, setWeightGrams] = useState("");
  const [tags, setTags] = useState("");

  const [hasVariants, setHasVariants] = useState(false);
  const [option1Name, setOption1Name] = useState("");
  const [option2Name, setOption2Name] = useState("");
  const [variants, setVariants] = useState<readonly VariantData[]>([]);

  const [imageUrls, setImageUrls] = useState<readonly { url: string; altText: string }[]>([]);
  const [newImageUrl, setNewImageUrl] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(`/api/products/${id}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const json = await res.json() as { success: boolean; data?: Record<string, unknown> };
        if (!json.success || !json.data) {
          navigate("/products");
          return;
        }
        const d = json.data as Record<string, unknown>;
        setName(d.name as string ?? "");
        setDescription(d.description as string ?? "");
        setProductType(d.productType as string ?? "physical");
        setStatus(d.status as string ?? "draft");
        setPrice(agorotToNis(d.price as number));
        setCompareAtPrice(agorotToNis(d.compareAtPrice as number));
        setCostPrice(agorotToNis(d.costPrice as number));
        setSeoTitle(d.seoTitle as string ?? "");
        setSeoDescription(d.seoDescription as string ?? "");
        setWeightGrams(d.weightGrams ? String(d.weightGrams) : "");
        setTags(d.tags as string ?? "");
        setHasVariants(d.hasVariants as boolean ?? false);
        setOption1Name(d.option1Name as string ?? "");
        setOption2Name(d.option2Name as string ?? "");

        const vs = d.variants as readonly Record<string, unknown>[] ?? [];
        setVariants(vs.map((v) => ({
          id: v.id as string,
          sku: v.sku as string ?? "",
          option1Value: v.option1Value as string ?? "",
          option2Value: v.option2Value as string ?? "",
          price: agorotToNis(v.price as number),
          compareAtPrice: agorotToNis(v.compareAtPrice as number),
          costPrice: agorotToNis(v.costPrice as number),
          stockQuantity: String(v.stockQuantity ?? 0),
          lowStockThreshold: String(v.lowStockThreshold ?? 5),
          weightGrams: v.weightGrams ? String(v.weightGrams) : "",
          barcode: v.barcode as string ?? "",
        })));

        const imgs = d.images as readonly Record<string, unknown>[] ?? [];
        setImageUrls(imgs.map((img) => ({
          url: img.url as string,
          altText: img.altText as string ?? "",
        })));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, accessToken, navigate]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      const body: Record<string, unknown> = {
        name,
        description: description || undefined,
        productType,
        status,
        price: nisToAgorot(price),
        compareAtPrice: compareAtPrice ? nisToAgorot(compareAtPrice) : undefined,
        costPrice: costPrice ? nisToAgorot(costPrice) : undefined,
        hasVariants,
        option1Name: hasVariants ? option1Name || undefined : undefined,
        option2Name: hasVariants ? option2Name || undefined : undefined,
        seoTitle: seoTitle || undefined,
        seoDescription: seoDescription || undefined,
        weightGrams: weightGrams ? parseInt(weightGrams, 10) : undefined,
        tags: tags || undefined,
        images: imageUrls.map((img) => ({ url: img.url, altText: img.altText || undefined })),
      };

      if (hasVariants && variants.length > 0) {
        body.variants = variants.map((v) => ({
          id: v.id || undefined,
          sku: v.sku || undefined,
          option1Value: v.option1Value || undefined,
          option2Value: v.option2Value || undefined,
          price: v.price ? nisToAgorot(v.price) : undefined,
          compareAtPrice: v.compareAtPrice ? nisToAgorot(v.compareAtPrice) : undefined,
          costPrice: v.costPrice ? nisToAgorot(v.costPrice) : undefined,
          stockQuantity: parseInt(v.stockQuantity, 10) || 0,
          lowStockThreshold: parseInt(v.lowStockThreshold, 10) || 5,
          weightGrams: v.weightGrams ? parseInt(v.weightGrams, 10) : undefined,
          barcode: v.barcode || undefined,
        }));
      }

      const url = isEdit ? `/api/products/${id}` : "/api/products";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json() as { success: boolean; error?: string };
      if (!json.success) {
        setError(json.error ?? "שגיאה בשמירה");
        return;
      }

      navigate("/products");
    } finally {
      setSaving(false);
    }
  }, [
    name, description, productType, status, price, compareAtPrice, costPrice,
    hasVariants, option1Name, option2Name, variants, imageUrls,
    seoTitle, seoDescription, weightGrams, tags, isEdit, id, accessToken, navigate,
  ]);

  const addImage = useCallback(() => {
    if (!newImageUrl.trim()) return;
    setImageUrls((prev) => [...prev, { url: newImageUrl.trim(), altText: "" }]);
    setNewImageUrl("");
  }, [newImageUrl]);

  const removeImage = useCallback((index: number) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/products")} className="min-h-[38px]">
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEdit ? "עריכת מוצר" : "מוצר חדש"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isEdit ? "ערוך את פרטי המוצר" : "הוסף מוצר חדש לחנות"}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 text-destructive text-sm p-3 rounded-lg mb-5 border border-destructive/20">
          <X className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto gap-1 h-auto p-1">
            <TabsTrigger value="general" className="gap-1.5 min-h-[38px]">
              <Package className="h-3.5 w-3.5" />
              כללי
            </TabsTrigger>
            <TabsTrigger value="pricing" className="gap-1.5 min-h-[38px]">
              <BarChart2 className="h-3.5 w-3.5" />
              מחירים
            </TabsTrigger>
            <TabsTrigger value="images" className="gap-1.5 min-h-[38px]">
              <ImagePlus className="h-3.5 w-3.5" />
              תמונות
            </TabsTrigger>
            <TabsTrigger value="seo" className="gap-1.5 min-h-[38px]">
              <Search className="h-3.5 w-3.5" />
              SEO
            </TabsTrigger>
          </TabsList>

          {/* General tab */}
          <TabsContent value="general" className="space-y-4 mt-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">פרטי מוצר</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="שם מוצר *">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="הזן שם מוצר..."
                    className="min-h-[42px]"
                  />
                </FormField>
                <FormField label="תיאור">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    placeholder="תיאור המוצר..."
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </FormField>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="סוג">
                    <select
                      value={productType}
                      onChange={(e) => setProductType(e.target.value)}
                      className="w-full h-[42px] rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="physical">פיזי</option>
                      <option value="digital">דיגיטלי</option>
                      <option value="service">שירות</option>
                    </select>
                  </FormField>
                  <FormField label="סטטוס">
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full h-[42px] rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="draft">טיוטה</option>
                      <option value="active">פעיל</option>
                      <option value="archived">בארכיון</option>
                    </select>
                  </FormField>
                </div>
                <FormField label="תגיות" hint="הפרד תגיות בפסיק">
                  <div className="relative">
                    <Tag className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="חולצות, אביזרים, מבצע"
                      className="ps-9 min-h-[42px]"
                    />
                  </div>
                </FormField>
              </CardContent>
            </Card>

            {/* Variant toggle */}
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasVariants}
                    onChange={(e) => setHasVariants(e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium">למוצר יש וריאנטים (מידה, צבע)</span>
                    <p className="text-xs text-muted-foreground mt-0.5">הפעל אם המוצר קיים בגרסאות שונות</p>
                  </div>
                </label>

                {hasVariants && (
                  <div className="mt-4 pt-4 border-t">
                    <VariantEditor
                      option1Name={option1Name}
                      option2Name={option2Name}
                      onOption1NameChange={setOption1Name}
                      onOption2NameChange={setOption2Name}
                      variants={variants}
                      onVariantsChange={setVariants}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing tab */}
          <TabsContent value="pricing" className="space-y-4 mt-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">מחירים</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                  כל המחירים כוללים מע"מ
                </p>
                <FormField label="מחיר (₪) *">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                    className="min-h-[42px]"
                    placeholder="0.00"
                  />
                </FormField>
                <FormField label="מחיר לפני הנחה (₪)" hint="יוצג כמחיר חצוי לצד המחיר הנוכחי">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={compareAtPrice}
                    onChange={(e) => setCompareAtPrice(e.target.value)}
                    className="min-h-[42px]"
                    placeholder="0.00"
                  />
                </FormField>
                <FormField label="עלות (₪)" hint="לשימוש פנימי בלבד, לא מוצג ללקוחות">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    className="min-h-[42px]"
                    placeholder="0.00"
                  />
                </FormField>
                <FormField label="משקל (גרם)">
                  <Input
                    type="number"
                    min="0"
                    value={weightGrams}
                    onChange={(e) => setWeightGrams(e.target.value)}
                    className="min-h-[42px]"
                    placeholder="0"
                  />
                </FormField>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images tab */}
          <TabsContent value="images" className="space-y-4 mt-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">תמונות מוצר</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  העלה תמונות דרך מדיה &rsaquo; העלאת תמונה והדבק כאן את הקישור
                </p>
                <div className="flex gap-2">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
                    placeholder="/api/images/abc123.jpg"
                    className="flex-1 min-h-[42px]"
                    dir="ltr"
                  />
                  <Button type="button" variant="outline" onClick={addImage} className="min-h-[42px]">
                    <ImagePlus className="h-4 w-4 me-1" />
                    הוסף
                  </Button>
                </div>

                {imageUrls.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {imageUrls.map((img, i) => (
                      <div key={`${img.url}-${i}`} className="relative group rounded-lg overflow-hidden">
                        <img
                          src={img.url}
                          alt={img.altText || `תמונה ${i + 1}`}
                          className="w-full aspect-square object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1.5 end-1.5 bg-destructive text-destructive-foreground rounded-full w-6 h-6 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {i === 0 && (
                          <span className="absolute bottom-1.5 start-1.5 bg-primary/90 text-primary-foreground text-xs px-1.5 py-0.5 rounded font-medium">
                            ראשית
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <ImagePlus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">אין תמונות עדיין</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO tab */}
          <TabsContent value="seo" className="space-y-4 mt-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">קידום אורגני (SEO)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField label="SEO כותרת" hint={`${seoTitle.length}/70 תווים`}>
                  <Input
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    maxLength={70}
                    className="min-h-[42px]"
                    placeholder="כותרת לתוצאות החיפוש..."
                  />
                </FormField>
                <FormField label="SEO תיאור" hint={`${seoDescription.length}/160 תווים`}>
                  <textarea
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    maxLength={160}
                    rows={3}
                    placeholder="תיאור לתוצאות החיפוש..."
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </FormField>

                {/* Preview */}
                {(seoTitle || seoDescription) && (
                  <div className="border rounded-lg p-4 bg-muted/30 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium mb-2">תצוגה מקדימה בגוגל</p>
                    <p className="text-blue-600 text-base font-medium truncate">{seoTitle || name}</p>
                    <p className="text-xs text-green-700 dark:text-green-500">example.co.il/products/{id ?? "new"}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{seoDescription || description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Submit */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/products")}
            className="min-h-[42px]"
          >
            ביטול
          </Button>
          <Button type="submit" disabled={saving} className="min-h-[42px]">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin me-1" />
            ) : (
              <Save className="h-4 w-4 me-1" />
            )}
            {isEdit ? "שמירה" : "יצירת מוצר"}
          </Button>
        </div>
      </form>
    </div>
  );
}
